import { ShieldCheck } from 'lucide-react'
import moment from 'moment'
import { parseAsString, useQueryState } from 'nuqs'
import { Link } from 'react-router'
import useSWR from 'swr'

import { CashRegisterNav } from '@/components/CashRegister/CashRegisterNav'
import { plural } from '@/components/CashRegister/registerUtils'
import { DatePaginator } from '@/components/DatePaginator/DatePaginator'
import { Money, PageHeader, StatStrip, StatusBadge } from '@/components/ds'
import { EetStatusBadge } from '@/components/Eet/EetStatusBadge'
import { EetWarningBanner } from '@/components/Eet/EetWarningBanner'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { EetStatus, EetSubmissionCollection } from '@invo.space/shared'

const environmentLabel: Record<string, string> = {
  playground: 'Playground',
  test: 'Testovací',
  production: 'Produkční',
}

export default function CashRegisterEetPage() {
  const [date] = useQueryState('date', parseAsString.withDefault(moment().format('YYYY-MM-DD')))

  const { data, isLoading } = useSWR<EetSubmissionCollection>(`/api/v2/eet-submissions?date=${date}&limit=100`, apiFetcher)
  const { data: status } = useSWR<EetStatus>('/api/v2/eet', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [
      { href: '/cash-register', children: 'Pokladna' },
      { href: '/cash-register/eet', children: 'EET 2.0' },
    ],
  })

  const submissions = data?.data ?? []
  const confirmed = submissions.filter((s) => s.status === 'confirmed').length
  const problems = submissions.filter((s) => s.status === 'rejected' || s.status === 'offline').length
  const sum = submissions.reduce((acc, s) => acc + Number(s.totalAmount || 0), 0)

  return (
    <>
      <title>EET 2.0</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <EetWarningBanner />
        <CashRegisterNav className="mb-5" />

        <PageHeader
          title="EET 2.0"
          description={
            status ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <StatusBadge status={status.enabled ? 'paid' : 'draft'} label={status.enabled ? 'Odesílání zapnuté' : 'Odesílání vypnuté'} />
                <span>Prostředí {environmentLabel[status.environment] ?? status.environment}</span>
                {status.verificationMode && <span>, ověřovací režim</span>}
              </span>
            ) : undefined
          }
          actions={
            <>
              <DatePaginator />
              <Button variant="outline" asChild>
                <Link to="/cash-register/settings">Nastavení EET 2.0</Link>
              </Button>
            </>
          }
        />

        <StatStrip
          className="mb-6"
          items={[
            { label: 'Evidovaná tržba', value: <Money amount={sum} minor={false} size="lg" />, hint: `${submissions.length} ${plural(submissions.length, 'záznam', 'záznamy', 'záznamů')}` },
            { label: 'Potvrzeno', value: <span className="text-2xl font-medium tabular">{confirmed}</span> },
            { label: 'Zamítnuto nebo offline', value: <span className={problems ? 'text-2xl font-medium text-danger tabular' : 'text-2xl font-medium tabular'}>{problems}</span> },
          ]}
        />

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="md:w-40">Účtenka</TableHead>
                <TableHead className="hidden w-44 md:table-cell">Datum tržby</TableHead>
                <TableHead className="hidden w-36 md:table-cell">Stav</TableHead>
                <TableHead className="hidden md:table-cell">POK</TableHead>
                <TableHead className="text-right md:w-36">Částka</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j} className={j === 0 || j === 4 ? undefined : 'hidden md:table-cell'}>
                        <Skeleton className="h-4 w-full max-w-32" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {submissions.map((submission) => (
                <TableRow key={submission.id} className="hover:bg-transparent">
                  <TableCell className="w-full max-w-0 font-mono text-xs md:w-auto md:max-w-none" title={submission.receiptId}>
                    {submission.receiptId.slice(0, 8)}
                    <p className="truncate font-sans text-xs text-muted-foreground tabular md:hidden">
                      {moment(submission.soldAt).format('D. M. YYYY, H:mm')}
                    </p>
                    {submission.errorMessage && (
                      <p className="truncate font-sans text-xs text-danger md:hidden">{submission.errorMessage}</p>
                    )}
                  </TableCell>
                  <TableCell className="hidden tabular md:table-cell">{moment(submission.soldAt).format('D. M. YYYY, H:mm')}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    <EetStatusBadge submission={submission} showPok={false} />
                    {submission.errorMessage && <p className="mt-1 text-xs text-danger">{submission.errorMessage}</p>}
                  </TableCell>
                  <TableCell className="hidden max-w-0 truncate font-mono text-xs text-muted-foreground md:table-cell">{submission.pok ?? '-'}</TableCell>
                  <TableCell className="text-right">
                    <Money amount={submission.totalAmount} minor={false} />
                    <div className="mt-0.5 md:hidden">
                      <EetStatusBadge submission={submission} showPok={false} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!isLoading && submissions.length === 0 && (
            <div className="p-6">
              <EmptyState icon={ShieldCheck}
                title="Pro tento den nejsou žádné EET 2.0 záznamy"
                description={status && !status.enabled ? 'Odesílání do EET 2.0 je vypnuté. Zapnete ho v nastavení pokladny.' : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
